import Papa from "papaparse";
import * as XLSX from "xlsx";

export interface ParsedSpreadsheet {
  fileName: string;
  fileSize: number;
  headers: string[];
  rows: any[][];
  rawRows: Record<string, any>[];
  totalRows: number;
}

export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
export const MAX_ROWS_ALLOWED = 50000;

/**
 * Lê e analisa um arquivo (.xlsx, .xls ou .csv)
 */
export async function parseSpreadsheetFile(
  file: File,
): Promise<ParsedSpreadsheet> {
  if (file.size > MAX_FILE_SIZE_BYTES) {
    throw new Error(
      `O arquivo excede o limite máximo permitido de 50 MB (tamanho atual: ${(file.size / 1024 / 1024).toFixed(1)} MB).`,
    );
  }

  const ext = file.name.split(".").pop()?.toLowerCase();

  if (ext === "csv" || ext === "txt") {
    return parseCsvFile(file);
  } else if (ext === "xlsx" || ext === "xls") {
    return parseExcelFile(file);
  } else {
    throw new Error(
      "Formato de arquivo não suportado. Por favor, envie uma planilha em formato .xlsx, .xls ou .csv.",
    );
  }
}

/**
 * Analisa arquivo CSV usando PapaParse com autodetecção de delimitador
 */
function parseCsvFile(file: File): Promise<ParsedSpreadsheet> {
  return new Promise((resolve, reject) => {
    Papa.parse<Record<string, any>>(file, {
      header: true,
      skipEmptyLines: "greedy",
      transformHeader: (header) => header.trim(),
      complete: (results) => {
        if (
          results.errors &&
          results.errors.length > 0 &&
          results.data.length === 0
        ) {
          reject(
            new Error(
              `Erro ao ler arquivo CSV: ${results.errors[0]?.message || "Formato inválido"}`,
            ),
          );
          return;
        }

        const headers = (results.meta.fields || [])
          .map((h) => h.trim())
          .filter(Boolean);
        if (headers.length === 0) {
          reject(
            new Error(
              "O arquivo não possui linha de cabeçalho válida com os nomes das colunas.",
            ),
          );
          return;
        }

        const rawRows = results.data;
        if (rawRows.length > MAX_ROWS_ALLOWED) {
          reject(
            new Error(
              `O arquivo possui ${rawRows.length.toLocaleString("pt-BR")} linhas, excedendo o limite de ${MAX_ROWS_ALLOWED.toLocaleString("pt-BR")} linhas por importação.`,
            ),
          );
          return;
        }

        const rows = rawRows.map((row) =>
          headers.map((h) =>
            row[h] !== undefined ? String(row[h]).trim() : "",
          ),
        );

        resolve({
          fileName: file.name,
          fileSize: file.size,
          headers,
          rows,
          rawRows,
          totalRows: rawRows.length,
        });
      },
      error: (err) => {
        reject(new Error(`Falha ao processar o arquivo CSV: ${err.message}`));
      },
    });
  });
}

/**
 * Analisa arquivo Excel (.xlsx / .xls) usando a biblioteca XLSX
 */
async function parseExcelFile(file: File): Promise<ParsedSpreadsheet> {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: "array" });

  if (!workbook.SheetNames || workbook.SheetNames.length === 0) {
    throw new Error("A planilha não contém nenhuma aba com dados.");
  }

  // Utiliza a primeira aba
  const firstSheetName = workbook.SheetNames[0];
  const worksheet = workbook.Sheets[firstSheetName];

  // Converte a planilha em matriz de linhas
  // cellDates: datas do Excel chegam como Date em vez do número serial (ex: 46296)
  const rawMatrix: any[][] = XLSX.utils.sheet_to_json(worksheet, {
    header: 1,
    defval: "",
    blankrows: false,
    raw: true,
  });
  const dateMatrix: any[][] = XLSX.utils.sheet_to_json(
    XLSX.read(buffer, { type: "array", cellDates: true }).Sheets[
      firstSheetName
    ],
    { header: 1, defval: "", blankrows: false, raw: true },
  );

  if (!rawMatrix || rawMatrix.length === 0) {
    throw new Error("A planilha está vazia.");
  }

  const formatCell = (val: any, dateVal: any): string => {
    if (val === undefined || val === null) return "";
    if (dateVal instanceof Date && !isNaN(dateVal.getTime())) {
      const pad = (n: number) => String(n).padStart(2, "0");
      const y = dateVal.getFullYear();
      const m = pad(dateVal.getMonth() + 1);
      const d = pad(dateVal.getDate());
      const hh = dateVal.getHours();
      const mm = dateVal.getMinutes();
      return hh || mm
        ? `${y}-${m}-${d} ${pad(hh)}:${pad(mm)}`
        : `${y}-${m}-${d}`;
    }
    return String(val).trim();
  };

  // A primeira linha com dados é o cabeçalho.
  // IMPORTANTE: guardamos o índice REAL de cada coluna. Colunas sem título
  // são ignoradas, mas sem deslocar os valores das colunas seguintes.
  const headerRow = rawMatrix[0] || [];
  const headerCols: Array<{ name: string; idx: number }> = [];
  const seen = new Map<string, number>();
  headerRow.forEach((cell: any, idx: number) => {
    let name = cell != null ? String(cell).trim() : "";
    if (!name) return;
    const count = seen.get(name) || 0;
    seen.set(name, count + 1);
    if (count > 0) name = `${name} (${count + 1})`;
    headerCols.push({ name, idx });
  });
  const headers: string[] = headerCols.map((h) => h.name);

  if (headers.length === 0) {
    throw new Error(
      "A planilha não possui um cabeçalho válido com os nomes das colunas na primeira linha.",
    );
  }

  const dataRowIdx: number[] = [];
  rawMatrix.slice(1).forEach((r, i) => {
    if (
      r.some((c) => c !== undefined && c !== null && String(c).trim() !== "")
    ) {
      dataRowIdx.push(i + 1);
    }
  });

  if (dataRowIdx.length > MAX_ROWS_ALLOWED) {
    throw new Error(
      `A planilha possui ${dataRowIdx.length.toLocaleString("pt-BR")} linhas, excedendo o limite de ${MAX_ROWS_ALLOWED.toLocaleString("pt-BR")} linhas.`,
    );
  }

  const rawRows: Record<string, any>[] = dataRowIdx.map((ri) => {
    const r = rawMatrix[ri] || [];
    const dr = dateMatrix[ri] || [];
    const obj: Record<string, any> = {};
    headerCols.forEach(({ name, idx }) => {
      obj[name] = formatCell(r[idx], dr[idx]);
    });
    return obj;
  });

  const rows = rawRows.map((row) => headers.map((h) => row[h] || ""));

  return {
    fileName: file.name,
    fileSize: file.size,
    headers,
    rows,
    rawRows,
    totalRows: rawRows.length,
  };
}

/**
 * Gera e dispara o download de uma planilha modelo de exemplo.
 * @param format - "xlsx" ou "csv"
 * @param importType - "person" | "company" | "deal" (default: "deal")
 */
export function downloadSampleTemplate(
  format: "xlsx" | "csv" = "xlsx",
  importType: "person" | "company" | "deal" = "deal",
) {
  let sampleData: Record<string, string>[];
  let fileName: string;

  if (importType === "company") {
    fileName = "modelo_organizacoes";
    sampleData = [
      {
        "Razao Social": "Tech Solucoes Digitais Ltda",
        CNPJ: "12.345.678/0001-90",
        Setor: "Tecnologia",
        "Faturamento Mensal": "150000",
        Telefone: "(11) 3333-4444",
        Site: "www.techsolucoes.com.br",
        Cidade: "Sao Paulo",
        UF: "SP",
        CEP: "01310-100",
        Endereco: "Av. Paulista, 1000",
        Responsavel: "Carlos Silva",
        Descricao: "Empresa especializada em desenvolvimento de sistemas.",
      },
      {
        "Razao Social": "Clinica Medica Bem Estar",
        CNPJ: "98.765.432/0001-10",
        Setor: "Saude",
        "Faturamento Mensal": "280000",
        Telefone: "(19) 3222-5566",
        Site: "www.clinicabemestar.med.br",
        Cidade: "Campinas",
        UF: "SP",
        CEP: "13013-001",
        Endereco: "Rua das Flores, 200",
        Responsavel: "Juliana Mendes",
        Descricao: "Clinica especializada em medicina preventiva.",
      },
      {
        "Razao Social": "Distribuidora Alianca de Alimentos",
        CNPJ: "34.567.890/0001-22",
        Setor: "Comercio Atacadista",
        "Faturamento Mensal": "650000",
        Telefone: "(31) 3444-7788",
        Site: "www.distribuidoraalianca.com.br",
        Cidade: "Belo Horizonte",
        UF: "MG",
        CEP: "30110-000",
        Endereco: "Rua do Comercio, 45",
        Responsavel: "Roberto Albuquerque",
        Descricao: "Distribuidora de alimentos para redes varejistas.",
      },
    ];
  } else if (importType === "person") {
    fileName = "modelo_pessoas";
    sampleData = [
      {
        "Nome Completo": "Carlos Eduardo Silva",
        "E-mail": "carlos.silva@techsolucoes.com.br",
        "Celular / WhatsApp": "(11) 98765-4321",
        Cargo: "Diretor Financeiro",
        Empresa: "Tech Solucoes Digitais Ltda",
        "E-mail 2": "",
        "Telefone 2": "",
        LinkedIn: "linkedin.com/in/carlos-silva",
        Responsavel: "Maria Vendedora",
        Observacoes: "Cliente desde 2022. Prefere contato por WhatsApp.",
      },
      {
        "Nome Completo": "Dra. Juliana Mendes",
        "E-mail": "juliana@clinicabemestar.med.br",
        "Celular / WhatsApp": "(19) 99888-7766",
        Cargo: "Socia Administradora",
        Empresa: "Clinica Medica Bem Estar",
        "E-mail 2": "juliana.mendes@gmail.com",
        "Telefone 2": "(19) 3222-5566",
        LinkedIn: "",
        Responsavel: "Joao Vendedor",
        Observacoes: "Prefere reunioes presenciais as tercas-feiras.",
      },
      {
        "Nome Completo": "Roberto Albuquerque",
        "E-mail": "roberto@distribuidoraalianca.com.br",
        "Celular / WhatsApp": "(31) 97766-5544",
        Cargo: "Gerente Geral",
        Empresa: "Distribuidora Alianca de Alimentos",
        "E-mail 2": "",
        "Telefone 2": "(31) 3444-7788",
        LinkedIn: "linkedin.com/in/roberto-albuquerque",
        Responsavel: "",
        Observacoes: "45 funcionarios, regime Lucro Real.",
      },
    ];
  } else {
    // "deal" — template completo com pessoa, empresa e negocio
    fileName = "modelo_negocios";
    sampleData = [
      {
        "Razão Social": "Tech Soluções Digitais Ltda",
        CNPJ: "12.345.678/0001-90",
        Setor: "Tecnologia",
        "Faturamento Mensal": "150000",
        Cidade: "São Paulo",
        UF: "SP",
        "Nome do Contato": "Carlos Eduardo Silva",
        "E-mail": "carlos.silva@techsolucoes.com.br",
        "Celular / WhatsApp": "(11) 98765-4321",
        Cargo: "Diretor Financeiro",
        "Título da Oportunidade": "Contrato BPO Financeiro + Contábil",
        "Valor da Proposta": "3500.00",
        "Etapa do Funil": "Proposta Enviada",
        "Origem do Lead": "Indicação",
        Temperatura: "Quente",
        "Segmento de Atuação": "Tecnologia / SaaS",
        "Complexidade do Projeto": "Média",
        "Data Prevista de Fechamento": "2026-10-15",
        "Próxima Ação": "Apresentação da Proposta Comercial",
        Etiquetas: "Prioritário; Indicação",
        Anotações: "Empresa no regime Lucro Presumido com 18 funcionários.",
      },
      {
        "Razão Social": "Clínica Médica Bem Estar",
        CNPJ: "98.765.432/0001-10",
        Setor: "Saúde",
        "Faturamento Mensal": "280000",
        Cidade: "Campinas",
        UF: "SP",
        "Nome do Contato": "Dra. Juliana Mendes",
        "E-mail": "juliana@clinicabemestar.med.br",
        "Celular / WhatsApp": "(19) 99888-7766",
        Cargo: "Sócia Administradora",
        "Título da Oportunidade": "Migração Contábil e Planejamento Tributário",
        "Valor da Proposta": "4200.00",
        "Etapa do Funil": "Reunião de Diagnóstico",
        "Origem do Lead": "Google Ads",
        Temperatura: "Quente",
        "Segmento de Atuação": "Saúde / Clínicas",
        "Complexidade do Projeto": "Alta",
        "Data Prevista de Fechamento": "2026-10-20",
        "Próxima Ação": "Reunião via Google Meet",
        Etiquetas: "Urgente; Saúde",
        Anotações:
          "Insatisfeitos com escritório anterior devido à lentidão nos retornos.",
      },
      {
        "Razão Social": "Distribuidora Aliança de Alimentos",
        CNPJ: "34.567.890/0001-22",
        Setor: "Comércio Atacadista",
        "Faturamento Mensal": "650000",
        Cidade: "Belo Horizonte",
        UF: "MG",
        "Nome do Contato": "Roberto Albuquerque",
        "E-mail": "roberto@distribuidoraalianca.com.br",
        "Celular / WhatsApp": "(31) 97766-5544",
        Cargo: "Gerente Geral",
        "Título da Oportunidade": "Gestão Fiscal e Folha de Pagamento",
        "Valor da Proposta": "5800.00",
        "Etapa do Funil": "Negociação",
        "Origem do Lead": "Prospecção Ativa",
        Temperatura: "Morno",
        "Segmento de Atuação": "Distribuição e Logística",
        "Complexidade do Projeto": "Média",
        "Data Prevista de Fechamento": "2026-10-30",
        "Próxima Ação": "Ligar para fechar contrato",
        Etiquetas: "B2B; Recorrência",
        Anotações: "45 funcionários, regime Lucro Real.",
      },
    ]; // end deal sampleData (inside else block)
  }

  if (format === "csv") {
    const csv = Papa.unparse(sampleData, { delimiter: ";" });
    const blob = new Blob(["\uFEFF" + csv], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `${fileName}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } else {
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Importação CRM");
    XLSX.writeFile(workbook, `${fileName}.xlsx`);
  }
}
